import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { Loader2, Layers, Link2, Zap } from 'lucide-react';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

interface AuthProps {
  initialMode?: 'login' | 'signup' | 'password-recovery';
  /** Pass true when the visitor had local data so an info banner is shown. */
  hasVisitorData?: boolean;
  /** Called with the actual mode used at submission time, before onAuthStateChange. */
  onAuthSuccess?: (mode: 'login' | 'signup') => void;
  /** VB-024F — Called after successful password update to exit the recovery shell. */
  onPasswordUpdateDone?: () => void;
}

export function Auth({
  initialMode = 'login',
  hasVisitorData = false,
  onAuthSuccess,
  onPasswordUpdateDone,
}: AuthProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // VB-024D — état post-signup : email de confirmation envoyé
  const [signupEmailSent, setSignupEmailSent] = useState(false);
  const [signupPendingEmail, setSignupPendingEmail] = useState('');
  // VB-024E — état post-signup : email déjà utilisé
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  // VB-024F — flux reset password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetPendingEmail, setResetPendingEmail] = useState('');
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(initialMode === 'password-recovery');
  const [passwordUpdateDone, setPasswordUpdateDone] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Bug 1: detect expired reset link (#error=access_denied&error_code=otp_expired),
  // clear the hash from the URL, and surface a clear message on the login form.
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (
      hashParams.get('error') === 'access_denied' &&
      hashParams.get('error_code') === 'otp_expired'
    ) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setShowPasswordUpdate(false);
      setIsLogin(true);
      setError(t('auth.link_expired'));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        trackEvent('login');
        onAuthSuccess?.('login');
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: {
              first_name: firstName,
              locale: i18n.language.startsWith('en') ? 'en' : 'fr',
            },
          },
        });
        if (error) throw error;
        // VB-024E — email déjà utilisé : Supabase renvoie user avec identities vides
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          setExistingEmail(email);
          setEmailAlreadyExists(true);
          setPassword('');
          return;
        }
        // VB-024D — afficher l'état de confirmation email au lieu de fermer silencieusement
        setSignupPendingEmail(email);
        setSignupEmailSent(true);
        trackEvent('signup');
        setPassword('');
      }
    } catch (err: any) {
      setError(err.message || t('errors.error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  // VB-024F — Envoi de l'email de réinitialisation
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setResetPendingEmail(email);
      setResetEmailSent(true);
      setEmail('');
    } catch (err: any) {
      setError(err.message || t('errors.error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  // VB-024F — Mise à jour du mot de passe après clic sur le lien email
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordUpdateDone(true);
      setNewPassword('');
    } catch (err: any) {
      setError(err.message || t('errors.error_occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8"
      style={{
        background:
          'radial-gradient(ellipse 90% 70% at 10% 20%, rgba(109,40,217,0.22) 0%, transparent 60%), radial-gradient(ellipse 65% 55% at 90% 80%, rgba(79,70,229,0.18) 0%, transparent 55%), radial-gradient(ellipse 50% 40% at 50% 50%, rgba(67,56,202,0.06) 0%, transparent 70%), #09090b',
      }}
    >
      <div className="w-full max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          {/* Colonne gauche — identité produit */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2.5 mb-8 lg:mb-12">
              <div className="w-9 h-9 rounded-xl bg-zinc-100 text-zinc-950 flex items-center justify-center font-bold text-lg shadow-[0_0_18px_rgba(139,92,246,0.30)]">
                V
              </div>
              <span className="text-zinc-100 font-medium">Visual Brain</span>
            </div>
            <h1 className="text-3xl lg:text-5xl font-semibold text-zinc-100 leading-tight mb-5">
              {t('auth.heading_takeback')}
              <br className="hidden sm:block" /> {t('auth.heading_of_your')}{' '}
              <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                {t('auth.heading_ideas')}
              </span>
            </h1>
            <p className="text-zinc-400 text-base leading-relaxed mb-10">{t('auth.subtitle')}</p>
            <div className="hidden lg:flex flex-col gap-5">
              {[
                {
                  Icon: Layers,
                  label: t('auth.feature_clarify'),
                  desc: t('auth.feature_clarify_desc'),
                },
                {
                  Icon: Link2,
                  label: t('auth.feature_connect'),
                  desc: t('auth.feature_connect_desc'),
                },
                { Icon: Zap, label: t('auth.feature_act'), desc: t('auth.feature_act_desc') },
              ].map(({ Icon, label, desc }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-3.5 h-3.5 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-200 leading-snug">{label}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Colonne droite — card auth + visiteur */}
          <div>
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-violet-500/10 rounded-2xl p-8 shadow-[0_0_0_1px_rgba(139,92,246,0.12),0_25px_60px_rgba(0,0,0,0.6),0_0_120px_rgba(109,40,217,0.14)]">
              {/* VB-024F — Mise à jour du mot de passe (retour depuis lien reset) */}
              {showPasswordUpdate ? (
                passwordUpdateDone ? (
                  <div className="space-y-5">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-6 h-6 text-zinc-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-zinc-100 text-sm font-medium leading-snug mb-3">
                        {t('auth.recovery_success')}
                      </p>
                      <p className="text-zinc-500 text-xs leading-relaxed">
                        {t('auth.recovery_success_info')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onPasswordUpdateDone?.()}
                      className="w-full bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors"
                    >
                      {t('auth.recovery_go_to_account')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="text-center mb-2">
                      <h2 className="text-lg font-medium text-zinc-100 mb-1">
                        {t('auth.recovery_heading')}
                      </h2>
                      <p className="text-zinc-500 text-xs">{t('auth.recovery_subtitle')}</p>
                    </div>
                    {error && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                        {error}
                      </div>
                    )}
                    <form onSubmit={handlePasswordUpdate} className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-zinc-400 mb-1">
                          {t('auth.recovery_password_label')}
                        </label>
                        <input
                          type="password"
                          required
                          minLength={6}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full bg-zinc-800/40 border border-zinc-700/50 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                          placeholder="••••••••"
                          autoFocus
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Mettre à jour
                      </button>
                    </form>
                  </div>
                )
              ) : emailAlreadyExists ? (
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-zinc-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                        />
                      </svg>
                    </div>
                    <p className="text-zinc-100 text-sm font-medium leading-snug mb-3">
                      {t('auth.email_taken')}
                    </p>
                    <p className="text-zinc-500 text-xs leading-relaxed mb-1">
                      {t('auth.account_exists_for')}
                    </p>
                    <p className="text-zinc-300 text-sm font-medium break-all mb-4">
                      {existingEmail}
                    </p>
                    <p className="text-zinc-600 text-xs">{t('auth.login_or_reset')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailAlreadyExists(false);
                      setIsLogin(true);
                      setError(null);
                    }}
                    className="w-full bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors"
                  >
                    {t('auth.login_button')}
                  </button>
                  {/* VB-024F — lien vers le flux reset password depuis l'écran email existant */}
                  <p className="text-center text-xs text-zinc-600">
                    <button
                      type="button"
                      onClick={() => {
                        setEmailAlreadyExists(false);
                        setEmail(existingEmail);
                        setError(null);
                        setShowForgotPassword(true);
                      }}
                      className="hover:text-zinc-400 transition-colors"
                    >
                      {t('auth.forgot_password')}
                    </button>
                  </p>
                </div>
              ) : signupEmailSent ? (
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-zinc-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5H4.5a2.25 2.25 0 00-2.25 2.25m19.5 0-9.75 6.75L2.25 6.75"
                        />
                      </svg>
                    </div>
                    <p className="text-zinc-100 text-sm font-medium leading-snug mb-3">
                      {t('auth.signup_confirmation')}
                    </p>
                    <p className="text-zinc-500 text-xs leading-relaxed mb-1">
                      {t('auth.signup_email_sent')}
                    </p>
                    <p className="text-zinc-300 text-sm font-medium break-all mb-4">
                      {signupPendingEmail}
                    </p>
                    <p className="text-zinc-600 text-xs">{t('auth.signup_check_spam')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSignupEmailSent(false);
                      setIsLogin(true);
                      setError(null);
                    }}
                    className="w-full bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors"
                  >
                    {t('auth.back_to_login')}
                  </button>
                </div>
              ) : resetEmailSent ? (
                /* VB-024F — confirmation email de réinitialisation envoyé */
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-6 h-6 text-zinc-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5H4.5a2.25 2.25 0 00-2.25 2.25m19.5 0-9.75 6.75L2.25 6.75"
                        />
                      </svg>
                    </div>
                    <p className="text-zinc-100 text-sm font-medium leading-snug mb-3">
                      {t('auth.reset_email_sent')}
                    </p>
                    <p className="text-zinc-500 text-xs leading-relaxed mb-1">
                      Un lien de réinitialisation a été envoyé à :
                    </p>
                    <p className="text-zinc-300 text-sm font-medium break-all mb-4">
                      {resetPendingEmail}
                    </p>
                    <p className="text-zinc-600 text-xs">
                      Pense aussi à vérifier tes spams / courriers indésirables.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setResetEmailSent(false);
                      setResetPendingEmail('');
                      setShowForgotPassword(false);
                      setIsLogin(true);
                      setError(null);
                    }}
                    className="w-full bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors"
                  >
                    {t('auth.back_to_login')}
                  </button>
                </div>
              ) : showForgotPassword ? (
                /* VB-024F — formulaire mot de passe oublié */
                <div className="space-y-5">
                  <div className="text-center mb-2">
                    <h2 className="text-lg font-medium text-zinc-100 mb-1">
                      Mot de passe oublié ?
                    </h2>
                    <p className="text-zinc-500 text-xs">{t('auth.forgot_subtitle')}</p>
                  </div>
                  {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-zinc-400 mb-1">
                        {t('auth.email_label')}
                      </label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-zinc-800/40 border border-zinc-700/50 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                        placeholder="votre@email.com"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      Envoyer le lien
                    </button>
                  </form>
                  <p className="text-center text-xs text-zinc-600">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setError(null);
                      }}
                      className="hover:text-zinc-400 transition-colors"
                    >
                      {t('auth.back_to_login_arrow')}
                    </button>
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <h2 className="text-lg font-semibold text-zinc-100 mb-1">
                      {isLogin ? t('auth.login_heading') : t('auth.signup_heading')}
                    </h2>
                    <p className="text-zinc-500 text-xs">
                      {isLogin ? t('auth.login_subtitle') : t('auth.signup_subtitle')}
                    </p>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  {/* VB-024B.3 — Visitor context info: shown only when coming from visitor mode with local data */}
                  {hasVisitorData && (
                    <div className="mb-4 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/60 text-zinc-400 text-xs leading-relaxed">
                      {isLogin ? t('auth.visitor_login_info') : t('auth.visitor_signup_info')}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                      <div>
                        <label className="block text-xs font-medium text-zinc-400 mb-1">
                          {t('auth.first_name_label')}
                        </label>
                        <input
                          type="text"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="w-full bg-zinc-800/40 border border-zinc-700/50 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                          placeholder={t('auth.first_name_placeholder')}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-zinc-400 mb-1">
                        {t('auth.email_label')}
                      </label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-zinc-800/40 border border-zinc-700/50 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-zinc-400 mb-1">
                        {t('auth.password_label')}
                      </label>
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-zinc-800/40 border border-zinc-700/50 rounded-lg px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                        placeholder="••••••••"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 font-medium py-2.5 rounded-lg text-sm transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {isLogin ? t('auth.login_button') : t('auth.signup_button')}
                    </button>
                  </form>

                  {isLogin && (
                    <p className="mt-4 text-center text-xs text-zinc-600">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setError(null);
                        }}
                        className="hover:text-zinc-400 transition-colors"
                      >
                        Mot de passe oublié ?
                      </button>
                    </p>
                  )}

                  <p className="mt-6 text-center text-xs text-zinc-500">
                    {isLogin
                      ? t('auth.no_account_yet') + ' '
                      : t('auth.already_have_account') + ' '}
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError(null);
                      }}
                      className="text-zinc-300 hover:text-zinc-100 underline underline-offset-2 transition-colors"
                    >
                      {isLogin ? t('auth.signup_button') : t('auth.login_button')}
                    </button>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
